import type { DmQuickReplyMedia } from '../../../../../services/dmQuickReplies';
import type { SendMessageAttachment } from '../../../../../services/dmMessageRequest';

export interface StagedFile {
    source: 'file';
    file: Blob;
    name: string;
    mimeType: string;
    size: number;
    replaced: string | null;
}

export interface StagedQuickReplyMedia {
    source: 'quick-reply';
    mediaId: number;
    title: string;
    mimeType: string;
    replaced: string | null;
}

export type StagedAttachment = StagedFile | StagedQuickReplyMedia;

export interface ValidatedFile {
    file: Blob;
    name: string;
    mimeType: string;
}

export function stagedAttachmentName(staged: StagedAttachment): string {
    return staged.source === 'file' ? staged.name : staged.title;
}

function isSameMedia(current: StagedAttachment | null, mediaId: number): boolean {
    return current?.source === 'quick-reply' && current.mediaId === mediaId;
}

export function stageFile(current: StagedAttachment | null, file: ValidatedFile): StagedFile {
    return {
        source: 'file',
        file: file.file,
        name: file.name,
        mimeType: file.mimeType,
        size: file.file.size,
        replaced: current ? stagedAttachmentName(current) : null
    };
}

export function stageQuickReplyMedia(
    current: StagedAttachment | null,
    media: DmQuickReplyMedia
): StagedQuickReplyMedia {
    const replacesOther = current !== null && !isSameMedia(current, media.id);
    return {
        source: 'quick-reply',
        mediaId: media.id,
        title: media.title,
        mimeType: media.mimeType,
        replaced: replacesOther ? stagedAttachmentName(current) : null
    };
}

export function canSendDraft(text: string, staged: StagedAttachment | null): boolean {
    return staged !== null || text.trim() !== '';
}

export function toSendAttachment(staged: StagedAttachment | null): SendMessageAttachment | null {
    if (!staged) return null;
    if (staged.source === 'quick-reply') {
        return { kind: 'quick-reply-media', mediaId: staged.mediaId };
    }
    return { kind: 'file', file: staged.file, fileName: staged.name };
}
